/**
 * Centralized status-gating logic for org-object actions menus (ER-11890).
 *
 * Replaces six independent implementations:
 * - RoleLineMenu / ResponsibilityLineMenu / DepartmentLineMenu (inline logic)
 * - JobFamiliesList/jobFamiliesListRow.js (rowActionsFor)
 * - JobCategoriesList/jobCategoriesListRow.js (rowActionsFor)
 * - useDropdownItems.js (getOrgObjectDropdownItems for Position)
 *
 * All six org objects (Position, Role, Responsibility, Department, Job Family,
 * Job Category) share one lifecycle (DRAFT → ACTIVE → RETIRED → ARCHIVED) and
 * one actions-menu contract per the Org Objects standard.
 */

const ACTION_META = {
  activate: {
    name: 'Activate',
    value: 'activate',
    icon: 'icons/dropdown/activate.svg',
    destructive: false,
  },
  delete: {
    name: 'Delete',
    value: 'delete',
    icon: 'icons/dropdown/trash.svg',
    destructive: true,
    negative: true,
  },
  retire: {
    name: 'Retire',
    value: 'retire',
    icon: 'icons/dropdown/activate.svg',
    destructive: false,
  },
  reactivate: {
    name: 'Reactivate',
    value: 'reactivate',
    icon: 'icons/dropdown/activate.svg',
    destructive: false,
  },
  rename: {
    name: 'Rename',
    value: 'rename',
    icon: 'icons/dropdown/rename.svg',
    destructive: false,
  },
  archive: {
    name: 'Archive',
    value: 'archive',
    icon: 'icons/dropdown/archive-new.svg',
    destructive: true,
    negative: true,
  },
  unarchive: {
    name: 'Unarchive',
    value: 'unarchive',
    icon: 'icons/dropdown/unarchive.svg',
    destructive: false,
  },
};

/**
 * Compute the allowed action set for a given org object type and status.
 *
 * @param {Object} options
 * @param {string} options.objectType - position|role|responsibility|department|job_family|job_category
 * @param {string} options.status - Draft|Active|Retired|Archived (case-insensitive)
 * @param {Object} [options.objectSpecificActions] - Optional array of object-specific actions to append
 * @returns {Array} MenuIcon-compatible action items [{name, value, icon, destructive?, negative?}]
 *
 * Rules per Org Objects standard:
 * - Rename: always available, any status
 * - Draft: Activate, Delete
 * - Active: Retire (no Delete/Activate/Reactivate/Archive/Unarchive)
 * - Retired: Reactivate, Archive
 * - Archived: Unarchive (no Delete)
 * - No Inactive/Deactivate branch exists
 */
export function getOrgObjectActions({ objectType, status, objectSpecificActions = [] }) {
  const normalizedStatus = String(status || 'draft').trim().toLowerCase();
  const actions = [];

  // Status-dependent actions (in standard menu order)
  if (normalizedStatus === 'draft') {
    actions.push(ACTION_META.activate);
    actions.push(ACTION_META.delete);
  } else if (normalizedStatus === 'active') {
    actions.push(ACTION_META.retire);
  } else if (normalizedStatus === 'retired') {
    actions.push(ACTION_META.reactivate);
    actions.push(ACTION_META.archive);
  } else if (normalizedStatus === 'archived') {
    actions.push(ACTION_META.unarchive);
  }

  // Rename is always available (any status)
  actions.push(ACTION_META.rename);

  // Append object-specific actions if provided
  if (Array.isArray(objectSpecificActions) && objectSpecificActions.length > 0) {
    actions.push(...objectSpecificActions);
  }

  return actions;
}

/**
 * Validate an object type against the six known org objects.
 * @param {string} objectType
 * @returns {boolean}
 */
export function isValidObjectType(objectType) {
  const VALID_TYPES = new Set([
    'position',
    'role',
    'responsibility',
    'department',
    'job_family',
    'job_category',
  ]);
  return VALID_TYPES.has(String(objectType || '').trim().toLowerCase());
}
